import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';

import { AppThunk } from '../../../index';
import { signUpFailed, signUpStarted, signUpSuccess } from '../reducer';
import { logInUser } from './logInUser';
import { COLLECTION } from '../../../../firestore/collectionNames';

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  termsAndPrivacyPolicy: boolean;
  uppdProcessingAgreement: boolean;
  lang: string;
  timezone: string;
  ip: string | null;
  phoneNumber: string | null;
}

async function getCountry(functions: firebase.functions.Functions): Promise<string | null> {
  try {
    const result = await functions.httpsCallable('users-getUserMetadata')();

    return result.data.body.country;
  } catch (_e) {
    return null;
  }
}

/** Dopisuje kraj w tle; błąd funkcji lub zapisu nie wpływa na rejestrację. */
async function saveCountry(
  userDoc: firebase.firestore.DocumentReference,
  functions: firebase.functions.Functions,
  updatedAt: firebase.firestore.FieldValue
): Promise<void> {
  try {
    const country = await getCountry(functions);
    if (country) {
      await userDoc.update({ country, updatedAt });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save user country:', e);
  }
}

export const signUpUser =
  (
    {
      email,
      password,
      lastName,
      firstName,
      termsAndPrivacyPolicy,
      uppdProcessingAgreement,
      lang,
      timezone,
      ip,
      phoneNumber,
    }: RegisterData,
    callback?: () => void,
    onFail?: () => void
  ): AppThunk =>
  async (dispatch, _, { auth, functions, firestore }) => {
    dispatch(signUpStarted());
    try {
      const res = await auth().createUserWithEmailAndPassword(email, password);
      if (!res.user) {
        throw new Error('Error when creating new account');
      }

      // Dokument powstaje od razu — kraj z funkcji chmurowej dopisujemy osobno (T-327),
      // żeby ProtectedRoute nie czekał na jej odpowiedź.
      const userDoc = firestore().collection(COLLECTION.USERS).doc(res.user.uid);
      await userDoc.set({
        firstName,
        lastName,
        email: email.toLowerCase(),
        ip,
        uid: res.user.uid,
        createdAt: firestore.FieldValue.serverTimestamp(),
        updatedAt: firestore.FieldValue.serverTimestamp(),
        termsAndPolicyAcceptDate: firestore.FieldValue.serverTimestamp(),
        termsAndPrivacyPolicy,
        uppdProcessingAgreement,
        uppdConsentAcceptDate: firestore.FieldValue.serverTimestamp(),
        lang,
        timezone,
        phoneNumber,
      });
      void saveCountry(userDoc, functions, firestore.FieldValue.serverTimestamp());
      dispatch(signUpSuccess());

      dispatch(logInUser({ email, password }));
      if (callback) {
        callback();
      }
    } catch (e) {
      const error = e as { code: string };
      dispatch(signUpFailed(error.code));

      if (onFail) {
        onFail();
      }
    }
  };
